import React, { useState, useEffect } from 'react';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Tooltip, IconButton, Typography, Badge, Toolbar } from '@mui/material';
import MuiAppBar from '@mui/material/AppBar';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../service/AuthContext';
import DialogProfile from './DialogProfile';
import DialogAnnouncement from './DialogAnnouncement';
import PopoverMessage from './PopoverMessage';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';
import { db } from '../service/Firebase-config';
import { onSnapshot, doc, getDoc } from '@firebase/firestore';
import logo from '../images/thumbnail_logo512D.png';

const drawerWidth = 240;

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const AppBarStudent = ({ open }) => {
  const { userInfo, setUserInfo, logout, currentUser } = useAuth();
  const { setSelectedUser, setTotalStars } = useGlobalContext();
  const { setOpenEditHw, setSelectedHw, setRating, setGroup } = useHomeworkContext();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotiBoardOpen, setIsNotiBoardOpen] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [newUpdates, setNewUpdates] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUpdateDetails = async (update) => {
      const studentRef = doc(db, 'students', update.studentId);
      const studentSnapshot = await getDoc(studentRef);
      const studentData = studentSnapshot.data();

      const homeworkRef = doc(db, `students/${update.studentId}/homeworks`, update.homeworkId);
      const homeworkSnapshot = await getDoc(homeworkRef);
      const homeworkData = { ...homeworkSnapshot.data(), id: homeworkSnapshot.id };

      const teacherRef = doc(db, `teachers`, update.teacherId);
      const teacherSnapshot = await getDoc(teacherRef);
      const teacherData = teacherSnapshot.data();
      return {
        ...update,
        student: studentData,
        homework: homeworkData,
        teacher: teacherData,
      };
    };

    if (currentUser?.uid) {
      const studentRef = doc(db, 'students', currentUser.uid);
      const unsubscribe = onSnapshot(studentRef, async (docSnapshot) => {
        const data = docSnapshot.data();
        if (data?.newUpdate) {
          const updatedDetails = await Promise.all(data.newUpdate.map(fetchUpdateDetails));
          setNewUpdates(updatedDetails);
        }
      });
      return () => unsubscribe();
    }
  }, [currentUser?.uid]);

  const handleLogout = async () => {
    setUserInfo(null);
    setSelectedUser(null);
    setTotalStars(0);
    setOpenEditHw(false);
    setSelectedHw(null);
    setRating(0);
    setGroup('students');
    await logout();
    navigate('/login/student');
  };

  const openProfile = () => setIsProfileOpen(true);
  const closeProfile = () => setIsProfileOpen(false);

  const openNotificationBoard = () => setIsNotiBoardOpen(true);
  const closeNotificationBoard = () => setIsNotiBoardOpen(false);

  const openNotificationList = (event) => setNotificationAnchorEl(event.currentTarget);
  const closeNotificationList = () => setNotificationAnchorEl(null);

  const isNotificationListOpen = Boolean(notificationAnchorEl);
  const notificationListId = isNotificationListOpen ? 'notification-popover' : undefined;

  return (
    <StyledAppBar sx={{ background: 'linear-gradient(180deg, #000000 30%, #283747 99%)' }} position="absolute" open={open}>
      <Toolbar sx={{ pr: '24px' }}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="open drawer"
          sx={{ marginRight: '36px', ...(open && { display: 'none' }) }}
        >
        </IconButton>
        <img src={logo} alt="logo" style={{ width: '40px', height: '40px', marginRight: '16px' }} />
        <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
          Welcome {currentUser.displayName}!
        </Typography>
        <Tooltip title="Message">
          <IconButton color="inherit" onClick={openNotificationList}>
            <Badge badgeContent={newUpdates.length} color="secondary">
              <MailOutlineIcon />
            </Badge>
          </IconButton>
        </Tooltip>
        <PopoverMessage
          notificationListId={notificationListId}
          isNotificationListOpen={isNotificationListOpen}
          notificationAnchorEl={notificationAnchorEl}
          closeNotificationList={closeNotificationList}
          newUpdates={newUpdates}
        />
        <Tooltip title="Announcement">
          <IconButton color="inherit" onClick={openNotificationBoard}>
            <NotificationsIcon />
          </IconButton>
        </Tooltip>
        <DialogAnnouncement open={isNotiBoardOpen} handleClose={closeNotificationBoard} />
        <Tooltip title="Profile">
          <IconButton color="inherit" onClick={openProfile}>
            <ManageAccountsIcon />
          </IconButton>
        </Tooltip>
        <DialogProfile open={isProfileOpen} handleClose={closeProfile} selectedUser={userInfo} setSelectedUser={setUserInfo} />
        <Tooltip title="Logout">
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>
    </StyledAppBar>
  );
};

export default AppBarStudent;
